import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 6,
  },
  colors: {
    primary: {
      main: "#35555C",
      dark: "#465A59",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#AB6E44",
      dark: "#875735",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#00AAE6",
      light: "#D6F1FB",
      infoBackground: "#EFE9E4",
    },
    text: {
      primary: "#333333",
      secondary: "#000000",
      hint: "#35555C",
    },
    background: "#FFFFFF",
    border: "#DBDBDB",
    hover: "#FAFAFA",
    placeholder: "#999999",
    transparent: "#00000000",
    divider: "#FAFAFA",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const volvia = {
  themeName: "Volvia",
  brand: BrandsEnum.Volvia,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    info: variables.colors.info,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    divider: variables.colors.border,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
  },
  typography: {
    fontFamily: getFontFamily("Volvo Novum-Regular"),
    color: variables.colors.text.primary,
    h1: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
    },
    h2: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      textAlign: "center" as "center",
      paddingBottom: "2rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h3: {
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 400,
      textDecoration: "underline",
    },
    h4: {
      color: variables.colors.text.primary,
      fontWeight: 400,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      fontSize: "1.25rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: 700,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.hover,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: variables.colors.hover,
    activeFontWeight: 500,
  },
  batteryElectricTextBox: {
    textColor: variables.colors.text.primary,
    fontFamily: getFontFamily("Volvo Novum-Regular"),
    fontWeight: 400,
    backgroundColor: variables.colors.info.infoBackground,
  },
  mileageInfoBox: {
    textColor: variables.colors.text.primary,
    fontFamily: getFontFamily("Volvo Novum-Regular"),
    fontWeight: 400,
    backgroundColor: variables.colors.info.infoBackground,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
    },
  },
  coverageSelect: {
    selected: {
      backgroundColor: variables.colors.divider,
    },
    descriptionCard: {
      borderColor: "#F2F5F6",
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  popup: {
    headerBackground: {
      background: "#EDF1F2",
    },
    root: {
      fontFamily: getFontFamily("Volvo Novum-Regular"),
    },
    h4: {
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      fontWeight: 600,
      color: variables.colors.primary.main,
    },
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    header: {
      backgroundColor: "#550077",
      textAlign: theme.typography.h1.textAlign,
      padding: `${theme.spacing(3)}px 0`,
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
        fontWeight: 700,
        "a&": {
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "none",
          fontWeight: 700,
        },
      },
      containedPrimary: {
        fontWeight: 700,
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        color: variables.colors.primary.main,
        border: "2px solid " + variables.colors.primary.main,
        fontWeight: 700,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&": {
          "&:hover": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          "&:active": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          color: variables.colors.primary.main,
          border: "2px solid " + variables.colors.primary.main,
          textDecoration: "none",
          fontWeight: 700,
        },
      },
      text: {
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.primary.main,
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
      // startIcon: buttonIcons,
      // endIcon: buttonIcons,
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontWeight: 700,
        fontFamily: getFontFamily("Volvo Novum-Medium"),
        "&$focused": {
          color: variables.colors.border,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        fontFamily: getFontFamily("Volvo Novum-Medium"),
        position: "relative" as PositionProperty,
        transform: "none",
        color: variables.colors.text.primary,
        lineHeight: "1.5rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        color: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.text.hint,
          textDecoration: "none",
          "&:hover": {
            color: variables.colors.text.primary,
          },
          "&:active": {
            color: variables.colors.text.hint,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.text.primary,
        fontWeight: 400,
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.placeholder,
        border: "1px solid " + variables.colors.border,
        "&$selected": {
          color: variables.colors.text.secondary,
          backgroundColor: variables.colors.divider,
          borderColor: variables.colors.primary.main,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiPickersDay: {
      daySelected: {
        "& :hover": {
          color: variables.colors.primary.contrastText,
        },
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
    MuiPaper: {
      root: {
        // ...Helpers.svgSelectorWrapper(svgIcons.close),
      },
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#EDF1F2",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#35555C",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#35555C",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  footerBar: {
    backgroundColor: "#1D1F20",
    textColor: variables.colors.secondary.contrastText,
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
    },
  },
  statusIndicator: {
    borderRadius: 4,
  },
  icons: {
    infoIcon: (
      <path
        {...defaultPathParams}
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='10'
        d='M 10.75,8.1503401 V 16.34966 m 10,-5.59966 c 0,5.522848 -4.477152,10 -10,10 -5.5228475,0 -10,-4.477152 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522848,0 10,4.4771525 10,10 M 11.402081,5.164 c 0,0.4120491 -0.334032,0.7460809 -0.746081,0.7460809 -0.412049,0 -0.7460809,-0.3340318 -0.7460809,-0.7460809 0,-0.4120491 0.3340319,-0.7460809 0.7460809,-0.7460809 0.412049,0 0.746081,0.3340318 0.746081,0.7460809'
      />
    ),
    helpIcon: (
      <path
        {...defaultPathParams}
        strokeDasharray='none'
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='4'
        strokeOpacity='1'
        fillOpacity='1'
        d='m 9.938,6.526 c 1.861,-0.823 4.275,-0.727 5.1,0.607 0.825,1.334 0.255,2.883 -1.157,4.079 -1.412,1.196 -1.88,1.847 -1.88,2.788 m 9.999019,-2 c 0,5.522847 -4.477153,10 -10,10 -5.5228475,0 -10,-4.477153 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522847,0 10,4.4771525 10,10 m -10.00002,4.914346 c -0.410685,0 -0.75167,0.340985 -0.75167,0.75167 0,0.03362 0.0017,0.06624 0.0059,0.09836 v 0.0015 c 0.04876,0.367965 0.368545,0.651804 0.7458,0.651804 0.320751,0 0.600313,-0.204508 0.706158,-0.493283 0.02965,-0.08092 0.04551,-0.168432 0.04551,-0.258387 0,-0.05665 -0.0071,-0.112575 -0.01909,-0.165897 -0.07589,-0.336227 -0.378404,-0.585773 -0.73258,-0.585773 z'
      />
    ),
    selectIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,9 12,15 6,9' />,
    arrowUpIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,15 12,9 6,15' />,
    dateTimeIcon: (
      <path
        {...defaultPathParams}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 2.4615,5.7769 h 20.577 V 21.5526 H 2.4615 Z m 20.577,3.4295 H 2.4615 M 17.5513,2.3474 V 5.7769 M 7.9487,2.3474 v 3.4295'
      />
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
          fill='#4D4D4D'
        />
        <path
          d='M12.001 12.222L16.279 16.5L16.501 16.278L12.223 12L16.501 7.722L16.279 7.5L12.001 11.778L7.72298 7.5L7.50098 7.722L11.779 12L7.50098 16.278L7.72298 16.5L12.001 12.222Z'
          fill='white'
          stroke='white'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
  <g id="Group_12436" data-name="Group 12436" transform="translate(-1060 -323.445)">
    <path id="Path_4742" data-name="Path 4742" d="M12,24A12,12,0,1,0,0,12,12,12,0,0,0,12,24Z" transform="translate(1084 323.445) rotate(90)" fill="#dde4e6" fill-rule="evenodd"/>
    <path id="Checkmark" d="M6.425,18.4,13.768,11l.232.23L6.425,18.866,2,14.4l.232-.23Z" transform="translate(1064 320.513)" fill="#333" stroke="#070708" stroke-width="0.5" fill-rule="evenodd"/>
  </g>
</svg>`),

    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    removeIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,18 6,6 M 18,6 6,18' />,

    crossIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,18 6,6 M 18,6 6,18' />,
    checkmarkIcon: <path {...defaultPathParams} d='M 4,12.714 9.143,17.857 20,7' />,

    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#35555C' stroke='#35555C' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <polygon id='Path' points='6 11.17 1.83 7 0.41 8.41 6 14 18 2 16.59 0.59' transform='translate(12 3) scale(0.5)' fill='#FFFFFF' />
      </>
    ),

    backIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='m 15,6 -6,6 6,6' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  divider: {
    backgroundColor: "#F4F4F4",
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(volvia);
